// ./client/src/frontend/components/Navbar.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  IconButton,
  alpha,
} from "@mui/material";
import { CloudDownload, Menu as MenuIcon, Close } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import ThemeToggle from "../../common/ThemeToggle";
import ChangeLang from "../../common/ChangeLang"; // ← ADD THIS
import { slideInUp } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const Navbar = ({ onToggleTheme, isDark, theme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { lang } = useContext(TranslateContext);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.hash]);

  const scrollToSection = (id) => {
    setMobileOpen(false);
    const isHome = location.pathname === "/";
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      history.push(`/#${id}`);
    }
  };

  const links = [
    { label: lang?.navFeatures || "Features", id: "features" },
    { label: lang?.navHowItWorks || "How It Works", id: "how-it-works" },
    { label: lang?.navPricing || "Pricing", id: "pricing" },
    { label: lang?.navTestimonials || "Testimonials", id: "testimonials" },
  ];

  return (
    <Box
      component="nav"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        transition: "all 0.3s ease",
        bgcolor: scrolled
          ? alpha(theme.palette.background.paper, 0.92)
          : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled
          ? `1px solid ${alpha(theme.palette.divider, 0.5)}`
          : "none",
        boxShadow: scrolled ? `0 4px 20px ${alpha("#000", 0.08)}` : "none",
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 1.75 }}
        >
          {/* Logo */}
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ cursor: "pointer" }}
            onClick={() => scrollToSection("hero") || history.push("/")}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <CloudDownload sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{ letterSpacing: "-0.02em" }}
            >
              TorrentCloud
            </Typography>
          </Stack>

          {/* Desktop links */}
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {links.map((l) => (
              <Typography
                key={l.id}
                variant="body2"
                fontWeight={500}
                color="text.secondary"
                sx={{
                  cursor: "pointer",
                  transition: "color 0.2s",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={() => scrollToSection(l.id)}
              >
                {l.label}
              </Typography>
            ))}
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* ← ADD ChangeLang here */}
            <Box sx={{ display: { xs: "none", sm: "flex" } }}>
              <ChangeLang />
            </Box>

            <ThemeToggle
              isDark={isDark}
              toggleColorMode={onToggleTheme}
              theme={theme}
              size="small"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => history.push("/user/login")}
              sx={{ borderRadius: 2, display: { xs: "none", sm: "flex" } }}
            >
              {lang?.signIn || "Sign In"}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => history.push("/user/register")}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              {lang?.getStarted || "Get Started"}
            </Button>
            <IconButton
              size="small"
              sx={{ display: { xs: "flex", md: "none" } }}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <Close /> : <MenuIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Mobile menu */}
        {mobileOpen && (
          <Box
            sx={{
              pb: 2,
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              gap: 1,
              animation: `${slideInUp} 0.2s ease`,
            }}
          >
            {links.map((l) => (
              <Typography
                key={l.id}
                variant="body2"
                fontWeight={500}
                color="text.secondary"
                sx={{
                  py: 0.75,
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={() => scrollToSection(l.id)}
              >
                {l.label}
              </Typography>
            ))}

            {/* ← ChangeLang in mobile menu too */}
            <Box sx={{ pt: 0.5 }}>
              <ChangeLang />
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => history.push("/user/login")}
              sx={{ borderRadius: 2, alignSelf: "flex-start" }}
            >
              {lang?.signIn || "Sign In"}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Navbar;
